import React, { useEffect, useState } from "react";
import { adminAxios } from "../api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ImageField from "../components/ImageField";

const blank = { name: "", image: "", rating: 5, text: "", location: "", display_order: 0, featured: false, published: true, active: true };

const TestimonialsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { setItems((await adminAxios.get("/testimonials")).data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating), display_order: Number(form.display_order) };
      if (editing === "new") await adminAxios.post("/testimonials", payload);
      else await adminAxios.put(`/testimonials/${editing}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const togglePublish = async (t) => { await adminAxios.patch(`/testimonials/${t.id}/publish`, { published: !t.published }); load(); };
  const del = async (t) => { if (!window.confirm("Delete this testimonial?")) return; await adminAxios.delete(`/testimonials/${t.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold text-slate-900">Testimonials</h1><p className="text-slate-500">Only published testimonials appear on the website</p></div>
        <Button onClick={() => { setForm(blank); setEditing("new"); }} className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">{t.image ? <img src={t.image} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-400 text-sm">{t.name?.charAt(0)}</span>}</div>
                  <div><div className="font-medium text-slate-800">{t.name}</div><div className="text-xs text-slate-400">{t.location}</div></div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${t.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{t.published ? "Published" : "Draft"}</span>
              </div>
              <div className="flex gap-0.5 my-2">{[...Array(Math.round(t.rating || 5))].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="text-sm text-slate-600 line-clamp-3">{t.text}</p>
              <div className="flex justify-end gap-1 mt-3">
                <button onClick={() => togglePublish(t)} className="p-2 rounded hover:bg-slate-100 text-slate-500">{t.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                <button onClick={() => { setForm({ ...blank, ...t }); setEditing(t.id); }} className="p-2 rounded hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(t)} className="p-2 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg bg-white rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4 flex items-center justify-between"><h2 className="font-semibold text-lg">{editing === "new" ? "New Testimonial" : "Edit Testimonial"}</h2><button onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4">
              <div><Label className="text-slate-600 text-sm mb-1.5 block">Name</Label><Input value={form.name} onChange={setF("name")} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-600 text-sm mb-1.5 block">Location</Label><Input value={form.location} onChange={setF("location")} /></div>
                <div><Label className="text-slate-600 text-sm mb-1.5 block">Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={setF("rating")} /></div>
              </div>
              <div><Label className="text-slate-600 text-sm mb-1.5 block">Profile Image</Label><ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="" compact /></div>
              <div><Label className="text-slate-600 text-sm mb-1.5 block">Testimonial</Label><Textarea rows={4} value={form.text} onChange={setF("text")} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-600 text-sm mb-1.5 block">Display Order</Label><Input type="number" value={form.display_order} onChange={setF("display_order")} /></div>
                <div className="flex items-end gap-4 pb-2">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdmin;
